"use client";

import { useTranslations } from "next-intl";
import { useEngineNotice } from "../lib/engine-notice";

/**
 * The engine release notice, one row under the toolbar (`docs/24` §10.1).
 *
 * The full text is `docs/records/2026-09-06_playground_engine_release_note.md`;
 * this is the short form: what changed, what went away, and a native
 * `<details>` for the version list so the row stays one line tall until a
 * reader asks. Not rendered inside the embed widget, which sits on somebody
 * else's page (`PlaygroundEmbedClient` never passes it to the layout).
 */
export function PlaygroundEngineNotice() {
  const t = useTranslations("playground");
  const { visible, dismiss } = useEngineNotice();
  if (!visible) return null;

  return (
    <section
      aria-labelledby="playground-engine-notice-title"
      data-testid="engine-notice"
      className="flex flex-wrap items-start justify-between gap-3 text-sm text-white/85"
    >
      <div className="min-w-0 flex-1 space-y-1.5">
        <p id="playground-engine-notice-title" className="font-semibold text-white">
          {t("engineNoticeTitle")}
        </p>
        <p className="text-white/75">{t("engineNoticeBody")}</p>
        <details className="group text-white/75">
          <summary className="inline-flex min-h-[44px] cursor-pointer list-none items-center gap-1 text-[#FFAB8F] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] sm:min-h-0">
            <span className="group-open:hidden">{t("engineNoticeDetailsShow")}</span>
            <span className="hidden group-open:inline">{t("engineNoticeDetailsHide")}</span>
          </summary>
          <ul className="mt-1.5 list-disc space-y-1 pl-5">
            <li>{t("engineNoticeVersions")}</li>
            <li>{t("engineNoticeFlags")}</li>
          </ul>
        </details>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="inline-flex min-h-[44px] items-center rounded-lg border border-white/15 px-3 text-sm font-semibold text-white/85 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] sm:min-h-[36px]"
      >
        {t("engineNoticeDismiss")}
      </button>
    </section>
  );
}
